import { createContext, useContext } from 'react'
import type { ComponentProps, ReactNode } from 'react'

import { AlertDialog as AlertDialogPrimitive } from '@base-ui/react/alert-dialog'
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'
import { tv, type VariantProps } from 'tailwind-variants'

import { cn } from '../../../utils/cn'
import { Icon } from '../../atoms/icon/icon'
import { textVariants } from '../../atoms/text/text'

/* Enter/exit choreography mirrors motion.dev's modal example: the popup
 * tilts in from a blurred, pushed-back 3D pose while the backdrop fades.
 * The 200ms enter delay lets the backdrop land first; the exit is faster
 * and undelayed. Reduced motion falls back to a plain fade. */
const popupVariants = tv(
  {
    base: [
      'bg-surface shadow-dialog relative max-w-full overflow-clip rounded-[16px] px-8 pb-10 pt-8',
      '[--dialog-pose-hidden:perspective(500px)_translateZ(-100px)_rotateY(25deg)_rotateX(5deg)]',
      '[--dialog-enter-ease:cubic-bezier(0.17,0.67,0.51,1)] [--dialog-exit-ease:cubic-bezier(0.67,0.17,0.62,0.64)]',
      'data-[ending-style]:opacity-0 data-[starting-style]:opacity-0',
      'motion-reduce:transition-opacity motion-reduce:duration-300 motion-reduce:ease-out',
      'motion-safe:blur-[0px] motion-safe:[transform:perspective(500px)_translateZ(0)_rotateY(0deg)_rotateX(0deg)]',
      'motion-safe:[transition:opacity_500ms_ease-out_200ms,filter_500ms_var(--dialog-enter-ease)_200ms,transform_500ms_var(--dialog-enter-ease)_200ms]',
      'motion-safe:data-[starting-style]:blur-[10px] motion-safe:data-[starting-style]:[transform:var(--dialog-pose-hidden)]',
      'motion-safe:data-[ending-style]:blur-[10px] motion-safe:data-[ending-style]:[transform:var(--dialog-pose-hidden)]',
      'motion-safe:data-[ending-style]:[transition:opacity_300ms_var(--dialog-exit-ease),filter_300ms_var(--dialog-exit-ease),transform_300ms_var(--dialog-exit-ease)]',
      '[&>[data-slot=dialog-actions]]:mt-6'
    ],
    variants: {
      orientation: {
        vertical: [
          'w-fit min-w-[520px]',
          '[&>[data-slot=dialog-close-btn]]:absolute [&>[data-slot=dialog-close-btn]]:right-8 [&>[data-slot=dialog-close-btn]]:top-8',
          /* Figma fixes the title at 408px in every vertical variant (the
           * 456px content width minus the 32px close button + 16px gap),
           * with or without the close button rendered. */
          '[&>[data-slot=dialog-title]]:w-[408px]',
          /* Fill×hug: only the actions row may widen the hugging card.
           * Capping every other child at the 520px card's content width
           * (456px) keeps text and form content from stretching it via
           * intrinsic sizing. */
          '[&>:not([data-slot=dialog-actions])]:max-w-[456px]',
          '[&>[data-slot=dialog-status-icon]+[data-slot=dialog-title]]:mt-4',
          '[&>[data-slot=dialog-title]+[data-slot=dialog-description]]:mt-2'
        ],
        horizontal: [
          /* Fixed anatomy: only the known slots are placed in the grid;
           * extra children fall to auto-placement and are unsupported. */
          'grid w-[920px] grid-cols-[auto_minmax(0,1fr)_auto]',
          '[&>[data-slot=dialog-status-icon]]:col-start-1 [&>[data-slot=dialog-status-icon]]:row-start-1 [&>[data-slot=dialog-status-icon]]:mr-[13px] [&>[data-slot=dialog-status-icon]]:self-center',
          '[&>[data-slot=dialog-title]]:col-start-2 [&>[data-slot=dialog-title]]:row-start-1 [&>[data-slot=dialog-title]]:self-center',
          '[&>[data-slot=dialog-close-btn]]:col-start-3 [&>[data-slot=dialog-close-btn]]:row-start-1 [&>[data-slot=dialog-close-btn]]:ml-4 [&>[data-slot=dialog-close-btn]]:self-center',
          '[&>[data-slot=dialog-description]]:col-start-2 [&>[data-slot=dialog-description]]:row-start-2 [&>[data-slot=dialog-description]]:mt-[6px]',
          '[&>[data-slot=dialog-actions]]:col-span-full [&>[data-slot=dialog-actions]]:row-start-3'
        ]
      }
    },
    defaultVariants: { orientation: 'vertical' }
  },
  { twMerge: false }
)

const statusIconVariants = tv(
  {
    base: 'flex size-10 items-center justify-center rounded-full border',
    variants: {
      status: {
        success: 'border-success-stroke bg-success-bg text-success-icon',
        warning: 'border-warning-stroke bg-warning-bg text-warning-icon',
        danger: 'border-danger-stroke bg-danger-bg text-danger-icon',
        info: 'border-information-stroke bg-information-bg text-information-icon'
      }
    }
  },
  { twMerge: false }
)

/* ---------------------------------- types --------------------------------- */

export type DialogStatusIconStatus = NonNullable<
  VariantProps<typeof statusIconVariants>['status']
>

export type DialogProps = DialogPrimitive.Root.Props & {
  /** When true, renders as an alert dialog (role="alertdialog") for
   *  destructive or critical actions that require explicit acknowledgment. */
  alert?: boolean
}

export type DialogOrientation = NonNullable<
  VariantProps<typeof popupVariants>['orientation']
>

export type DialogContentProps = DialogPrimitive.Popup.Props & {
  orientation?: DialogOrientation
  children: ReactNode
  /**
   * Merged onto the backdrop and viewport layers (both `z-50`). Lets a dialog
   * opened from a higher stacking context, e.g. the Alti panel at `z-[999]`,
   * stack above it: `layerClassName='z-[1000]'`.
   */
  layerClassName?: string
}

export interface DialogStatusIconProps {
  status: DialogStatusIconStatus
  /** Material Symbols glyph. Defaults to the status' canonical icon. */
  name?: string
  className?: string
}

export type DialogActionsProps = ComponentProps<'div'> & {
  layout?: 'horizontal' | 'vertical'
}

/* -------------------------------- context --------------------------------- */

const IsAlertContext = createContext(false)

function usePrimitives() {
  const isAlert = useContext(IsAlertContext)
  return isAlert ? AlertDialogPrimitive : DialogPrimitive
}

/* ------------------------------- components ------------------------------- */

const statusIconName: Record<DialogStatusIconStatus, string> = {
  success: 'check_circle',
  warning: 'warning',
  danger: 'error',
  info: 'info'
}

function Dialog({ alert = false, ...props }: DialogProps) {
  const Primitive = alert ? AlertDialogPrimitive : DialogPrimitive
  return (
    <IsAlertContext.Provider value={alert}>
      <Primitive.Root data-slot='dialog' {...props} />
    </IsAlertContext.Provider>
  )
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  const P = usePrimitives()
  return <P.Trigger data-slot='dialog-trigger' {...props} />
}

function DialogContent({
  orientation = 'vertical',
  className,
  layerClassName,
  children,
  ...props
}: DialogContentProps) {
  const P = usePrimitives()
  return (
    <P.Portal>
      <P.Backdrop
        data-slot='dialog-backdrop'
        className={cn(
          'bg-dialog-bg fixed inset-0 z-50',
          'transition-opacity duration-300 ease-out',
          'data-[starting-style]:opacity-0',
          'data-[ending-style]:opacity-0',
          layerClassName
        )}
      />
      <P.Viewport
        data-slot='dialog-viewport'
        className={cn(
          'fixed inset-0 z-50 flex items-center justify-center overflow-auto p-4',
          layerClassName
        )}
      >
        <P.Popup
          data-slot='dialog-popup'
          className={cn(popupVariants({ orientation }), className)}
          {...props}
        >
          {children}
        </P.Popup>
      </P.Viewport>
    </P.Portal>
  )
}

function DialogStatusIcon({ status, name, className }: DialogStatusIconProps) {
  return (
    <div
      data-slot='dialog-status-icon'
      className={cn(statusIconVariants({ status }), className)}
    >
      <Icon name={name ?? statusIconName[status]} className='text-[16px]' />
    </div>
  )
}

function DialogTitle({
  className,
  children,
  ...props
}: DialogPrimitive.Title.Props) {
  const P = usePrimitives()
  return (
    <P.Title
      data-slot='dialog-title'
      className={cn(
        textVariants({ variant: 'title-xl' }),
        'text-surface-title-50',
        className
      )}
      {...props}
    >
      {children}
    </P.Title>
  )
}

function DialogDescription({
  className,
  children,
  ...props
}: DialogPrimitive.Description.Props) {
  const P = usePrimitives()
  return (
    <P.Description
      data-slot='dialog-description'
      className={cn(
        textVariants({ variant: 'body-lg' }),
        'text-surface-body',
        className
      )}
      {...props}
    >
      {children}
    </P.Description>
  )
}

function DialogActions({
  layout = 'horizontal',
  className,
  children,
  ...props
}: DialogActionsProps) {
  return (
    <div
      data-slot='dialog-actions'
      className={cn(
        layout === 'horizontal'
          ? 'flex flex-row items-center justify-end gap-2'
          : 'flex w-full flex-col gap-2',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

/** Left-aligned slot inside `DialogActions` (e.g. a "Don't show again"
 *  checkbox), shown opposite the buttons in the horizontal layout. */
function DialogActionsStart({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot='dialog-actions-start'
      className={cn('mr-auto', className)}
      {...props}
    />
  )
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  const P = usePrimitives()
  return <P.Close data-slot='dialog-close' {...props} />
}

function DialogCloseButton({
  className,
  ...props
}: Omit<DialogPrimitive.Close.Props, 'children'>) {
  const P = usePrimitives()
  return (
    <P.Close
      data-slot='dialog-close-btn'
      aria-label='Close dialog'
      className={cn(
        'text-surface-title-50 flex size-8 cursor-pointer items-center justify-center',
        className
      )}
      {...props}
    >
      <Icon name='close' size='lg' />
    </P.Close>
  )
}

export {
  Dialog,
  DialogActions,
  DialogActionsStart,
  DialogClose,
  DialogCloseButton,
  DialogContent,
  DialogDescription,
  DialogStatusIcon,
  DialogTitle,
  DialogTrigger
}
